import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Button, buttonClassName } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { ResetPasswordResult } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

import { AuthScreenLayout } from "./AuthScreenLayout";

/**
 * `/reset-password?token=...` (gap-fill spec, screen 4) — `POST
 * /auth/reset-password`. Same query-param convention as
 * `AcceptInvitation.tsx`, hardcoded by the reset email's own link
 * (`app/domain/identity/password_reset.py:181`,
 * `f"{app_base_url}/reset-password?token={token}"`) — `token` is read from
 * `useSearchParams()` and sent unmodified (VC-GF1-09).
 *
 * `POST /auth/reset-password` returns only `{reset: true}` — no session
 * (`ResetPasswordResult`'s own shape). Consuming the token also revokes
 * every session the account holds server-side
 * (`password_reset.py`'s own doc comment) — a reason beyond consistency with
 * `AcceptInvitation.tsx` not to auto-sign-in here: the person resetting a
 * password may not be the same person who was signed in a moment ago.
 * Success is a plain confirmation plus a real link to `/sign-in`
 * (judgement call 3, VC-GF1-06).
 *
 * A refused token renders `password_reset.RESET_REFUSAL_MESSAGE` verbatim,
 * the same one sentence whatever the real cause (AC, VC-GF1-04).
 */
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reset, setReset] = useState(false);

  const mismatch = confirmPassword !== "" && password !== confirmPassword;
  const canSubmit = token !== "" && password.length >= 8 && password === confirmPassword;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      await apiFetch<ResetPasswordResult>("/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      setReset(true);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (reset) {
    return (
      <AuthScreenLayout title="Password reset">
        <p className="text-body text-ink-secondary">
          Your password has been reset. Sign in with your email address and new password to get started.
        </p>
        <Link to="/sign-in" className={buttonClassName("primary")}>
          Go to sign in
        </Link>
      </AuthScreenLayout>
    );
  }

  if (token === "") {
    return (
      <AuthScreenLayout title="Reset link incomplete">
        <p role="alert" className="text-body text-danger">
          This password reset link is missing its token. Please use the link from the reset email, or request a
          new one.
        </p>
        <Link to="/forgot-password" className={buttonClassName("secondary")}>
          Request a new link
        </Link>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout title="Set a new password">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("password")}</span>
          <Input
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">Confirm password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </label>

        {mismatch ? (
          <p role="alert" className="text-meta text-danger">
            Those passwords don&apos;t match.
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? "Resetting…" : "Reset password"}
        </Button>
      </form>
    </AuthScreenLayout>
  );
}
